import type { RenderMode } from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { Settings } from "lucide-react";
import { RenderUnitBadge } from "./RenderUnitBadge";

export function TemplateRenderPlaceholder({
    unitId,
    kindLabel,
    technicalLabel,
    params,
    statusLabel,
    message,
    paramLabel,
    mode,
    onConfigure,
    configureLabel,
    badgeMain,
    badgeSub,
    badgeSup,
}: {
    unitId: string;
    kindLabel: string;
    technicalLabel?: string | null;
    params?: string[];
    statusLabel?: string;
    message?: string;
    paramLabel?: string | null;
    mode: RenderMode;
    onConfigure?: () => void;
    configureLabel?: string;
    badgeMain: string;
    badgeSub?: string | null;
    badgeSup?: string | null;
}) {
    const t = useT("editor");
    const resolvedStatusLabel = statusLabel ?? t("render.template.status", { kind: kindLabel });
    const resolvedMessage = message ?? t("render.template.message");
    const resolvedConfigureLabel = configureLabel ?? t("render.template.configure");
    const resolvedParamLabel = paramLabel ?? (params && params.length > 0
        ? t("render.params", { params: params.join(", ") })
        : null);

    if (mode === "inline") {
        return (
            <span
                className="inline-flex items-center gap-1 rounded border border-dashed border-border bg-muted/30 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                data-magic-template-render-placeholder="true"
                title={resolvedMessage}
            >
                <RenderUnitBadge
                    main={badgeMain}
                    sub={badgeSub}
                    sup={badgeSup}
                    template
                    size="sm"
                />
                <code className="font-mono">{unitId}</code>
                <span>{resolvedStatusLabel}</span>
            </span>
        );
    }

    return (
        <div
            className="my-1.5 rounded-lg border border-dashed border-border bg-muted/25 px-3 py-2.5"
            data-magic-template-render-placeholder="true"
        >
            <div className="flex items-start gap-3">
                <RenderUnitBadge
                    main={badgeMain}
                    sub={badgeSub}
                    sup={badgeSup}
                    template
                    size="sm"
                    className="mt-0.5 bg-background"
                />
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <code className="font-mono text-xs text-foreground">{unitId}</code>
                        <span className="text-xs font-medium text-muted-foreground">
                            {resolvedStatusLabel}
                        </span>
                        {technicalLabel ? (
                            <span className="text-[10px] text-muted-foreground/50">
                                {technicalLabel}
                            </span>
                        ) : null}
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground/70">
                        {resolvedMessage}
                    </div>
                    {resolvedParamLabel ? (
                        <div className="mt-1 truncate text-[10px] text-muted-foreground/50">
                            {resolvedParamLabel}
                        </div>
                    ) : null}
                </div>
                {onConfigure ? (
                    <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        onClick={onConfigure}
                    >
                        <Settings size={10} />
                        {resolvedConfigureLabel}
                    </button>
                ) : null}
            </div>
        </div>
    );
}
